// Settings → Zero-trust (impire-hq 02-DESIGN/03-console/01-the-console.md
// § Settings: the Zero-trust tab; 02-DESIGN/02-secrets/03-escrow.md § what the
// console shows), an admin's: what the recovery shares are and why they exist,
// the current set, the three shares in a table, Collect — the tab's primary
// action while the workspace's own share waits — New shares and Present, each
// with when a person needs it, and an open recovery. *Collect*'s one-time
// share and *Present* each stand in the tab's place (01-the-console.md §
// conventions: a form); Back closes either, and a share closed is gone for
// good. *New shares* is asked in a confirmation. Nothing here shows a
// share after the moment it is collected, and nothing shows a key, ever. The
// words are zero-trust.ts's; the parts — the set, the shares, an open
// recovery, Collect's one-time share and Present — are shares.tsx's, which a
// child's Zero-trust tab under Workspaces draws too (spec 061).

import { useState } from "react";

import { Button, NotAllowed, Notice, Pending } from "../../ui/components.tsx";
import { useFormState } from "../../ui/form.tsx";
import { ConfirmDialog } from "../../ui/table.tsx";
import { EscrowAction, escrowSubject, Slot, type EscrowStatus } from "../../wire/escrow.ts";
import { SERVICE } from "../../wire/identity.ts";
import { useSurface } from "../surface.tsx";
import {
  Ceremony,
  OneTimeShare,
  PresentAct,
  PresentForm,
  Shares,
  ShareSet,
  useShares,
  zeroTrustFormDrawn,
  type ZeroTrustState,
} from "./shares.tsx";
import {
  collectedLine,
  explainer,
  ISSUE_ANEW_WHILE_OPEN,
  issueAnewConsequence,
  issueAnewLine,
  NEW_SHARES,
  NEW_SHARES_TITLE,
  NEW_SHARES_VERB,
  outcomeAnswer,
  PRESENT_FORM,
  presenterWords,
  presentLine,
  refusalAnswer,
  replacedLine,
  slotWords,
  type Holders,
  type ZeroTrustForm,
} from "./zero-trust.ts";

export { CollectAction, zeroTrustFormDrawn, type ZeroTrustState } from "./shares.tsx";
export type { ZeroTrustForm } from "./zero-trust.ts";

/** Act is an escrow action Settings' tab takes, named so the verb inventory reads which. */
type Act = typeof EscrowAction.collect | typeof EscrowAction.resplit | typeof EscrowAction.present;

/** OwnZeroTrust is Settings' tab's state: the workspace's own shares, which it may also re-split. */
export interface OwnZeroTrust extends ZeroTrustState {
  resplit: () => Promise<unknown>;
}

/** useZeroTrust reads the workspace's own escrow status and holds the acts on it. */
export function useZeroTrust(): OwnZeroTrust {
  const s = useSurface();
  const call = (action: Act, body: unknown) => s.bus.call<unknown>(SERVICE, escrowSubject(action), body);
  const z = useShares(
    "escrow/status",
    () => s.bus.call<EscrowStatus>(SERVICE, escrowSubject(EscrowAction.status), undefined),
    call,
  );
  return {
    ...z,
    resplit: () => z.run(() => call(EscrowAction.resplit, undefined)),
  };
}

export function ZeroTrust({ z, holders }: { z: OwnZeroTrust; holders: Holders }) {
  const s = useSurface();
  const form = useFormState<ZeroTrustForm>();
  const [issuing, setIssuing] = useState(false);
  const [outcome, setOutcome] = useState<string | undefined>(undefined);
  const { st } = z;
  const name = holders.name;
  const present = () => {
    setOutcome(undefined);
    form.show(PRESENT_FORM);
  };
  if (z.collected !== undefined) {
    return (
      <OneTimeShare title={`${name}'s recovery share`} reply={z.collected} onClose={z.closeCollected}>
        {collectedLine(name)}
      </OneTimeShare>
    );
  }
  if (st.state === "ready" && zeroTrustFormDrawn(form.open, false, st.value)) {
    return (
      <PresentForm
        z={z}
        words={{
          title: `Present ${name}'s share`,
          line: presentLine(name),
          field: `${name}'s share`,
          slug: s.tenant,
          name,
          refusal: refusalAnswer,
        }}
        opens={st.value.ceremony === undefined}
        onClose={form.close}
        onDone={(o) => {
          form.close();
          setOutcome(outcomeAnswer(o, name));
        }}
      />
    );
  }
  return (
    <div className="stack">
      <div className="card">
        <p>{explainer(holders)}</p>
      </div>
      {st.state === "pending" && <Pending what="Reading" />}
      {st.state === "refused" && <NotAllowed err={st.err} what="Zero-trust" />}
      {st.state === "ready" && st.value.set === undefined && (
        <p className="muted">No recovery shares have been issued for {name} yet.</p>
      )}
      {st.state === "ready" && st.value.set !== undefined && (
        <>
          {outcome !== undefined && <Notice kind="ok">{outcome}</Notice>}
          {st.value.ceremony !== undefined && (
            <Ceremony status={st.value} holders={holders} who={(p) => presenterWords(p, true)} />
          )}
          <ShareSet status={st.value} name={name} replaced={replacedLine(st.value, name)} />
          {z.err !== undefined && <NotAllowed err={z.err} what="Zero-trust" />}
          <Shares
            status={st.value}
            holders={holders}
            mine={Slot.admin}
            words={(r) => slotWords(r, holders)}
            busy={z.busy}
            onCollect={z.collect}
            onPresent={present}
          />
          <div className="card stack">
            <div>
              <Button
                kind="default"
                disabled={z.busy || st.value.ceremony !== undefined}
                onClick={() => {
                  setIssuing(true);
                }}
              >
                {NEW_SHARES}
              </Button>
              <p className="small muted">
                {st.value.ceremony !== undefined ? ISSUE_ANEW_WHILE_OPEN : issueAnewLine(name)}
              </p>
            </div>
            <PresentAct busy={z.busy} onPresent={present} line={presentLine(name)} />
          </div>
          {issuing && (
            <ConfirmDialog
              ask={{
                title: NEW_SHARES_TITLE,
                consequence: issueAnewConsequence(st.value, holders),
              }}
              verb={NEW_SHARES_VERB}
              onCancel={() => {
                setIssuing(false);
              }}
              onConfirm={() => {
                setIssuing(false);
                z.closeCollected();
                z.resplit().catch(() => undefined);
              }}
            />
          )}
        </>
      )}
    </div>
  );
}
